import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n/LanguageContext';
import { slugFor } from './slugs';

/**
 * Turns an authored href into one for the active locale.
 *
 * Content is authored with links like `/blog/what-is-manifestation`, where the
 * last segment is the post *id*. Every locale writes the same id, so a single
 * translated sentence never has to know the Turkish or German slug — this maps
 * it: `/blog/what-is-manifestation` -> `/tr/blog/manifestasyon-nedir`.
 */
export function useHrefResolver() {
  const { locale, path } = useI18n();

  return useCallback(
    (href) => {
      if (!href.startsWith('/')) return href;
      const post = href.match(/^\/blog\/([^/#?]+)$/);
      if (post) {
        const slug = slugFor(post[1], locale) || post[1];
        return path(`/blog/${slug}`);
      }
      return path(href);
    },
    [locale, path]
  );
}

/** Renders **bold**, *italic* and [text](url) inside a content string. */
export function renderInline(text, resolveHref = (h) => h, linkClass) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const [, label, rawHref] = linkMatch;
      const href = resolveHref(rawHref);
      if (href.startsWith('/')) {
        return (
          <Link key={i} to={href} className={linkClass}>
            {label}
          </Link>
        );
      }
      return (
        <a key={i} href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
          {label}
        </a>
      );
    }
    return part;
  });
}

/** renderInline bound to the active locale. */
export function useInline(linkClass) {
  const resolve = useHrefResolver();
  return useCallback((text) => renderInline(text, resolve, linkClass), [resolve, linkClass]);
}

/**
 * Renders one authored content block. Shared by blog posts and legal pages;
 * `classes` lets each caller pass its own CSS module, and `renderCta` lets the
 * blog drop its app promo in without this module importing it.
 */
export function ContentBlock({ block, classes = {}, renderCta }) {
  const inline = useInline(classes.link);

  switch (block.t) {
    case 'h2':
      return <h2>{inline(block.x)}</h2>;
    case 'h3':
      return <h3>{inline(block.x)}</h3>;
    case 'p':
      return <p className={classes.paragraph}>{inline(block.x)}</p>;
    case 'ul':
      return (
        <ul className={classes.list}>
          {block.x.map((item, i) => (
            <li key={i}>{inline(item)}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol className={classes.list}>
          {block.x.map((item, i) => (
            <li key={i}>{inline(item)}</li>
          ))}
        </ol>
      );
    case 'quote':
      return <blockquote>{inline(block.x)}</blockquote>;
    case 'table':
      return (
        <div className={classes.tableContainer}>
          <table className={classes.styledTable}>
            <thead>
              <tr>
                {block.head.map((cell, i) => (
                  <th key={i}>{inline(cell)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j}>{inline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'cards':
      return (
        <div className={classes.dataGrid}>
          {block.x.map((card, i) => (
            <div key={i} className={classes.dataCard}>
              <h3>{inline(card.h)}</h3>
              <p>{inline(card.p)}</p>
            </div>
          ))}
        </div>
      );
    case 'cta':
      return renderCta ? renderCta() : null;
    default:
      return null;
  }
}

/**
 * Splits a flat block list into h2-delimited groups, so the legal pages keep
 * their existing `.section` card styling without the authored data having to
 * describe nesting.
 */
export function groupBySection(blocks) {
  const groups = [];
  let current = [];
  blocks.forEach((block) => {
    if (block.t === 'h2' && current.length > 0) {
      groups.push(current);
      current = [];
    }
    current.push(block);
  });
  if (current.length > 0) groups.push(current);
  return groups;
}
